import {
  ArrowUpOutlined,
  DeleteOutlined,
  FileImageOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Button, Form, Input, Modal } from "antd";
import * as React from "react";
import { ImagePreview } from "src/components/ImagePreview";
import { ZSidLabel } from "src/types";

import * as styles from "./ExternalImagesModal.module.less";
import { KeyedImgFrame } from "./KeyedImgFrame";

/* eslint react/no-unstable-nested-components: "off" */

type PropsExternalImagesModal = {
  srcValue: KeyedImgFrame[];
  onChange(newValue: ZSidLabel[]): void;
  open: boolean;
  close: () => void;
  maxCount: number | undefined;
};

type FormData = {
  items: ZSidLabel[];
};

export const ExternalImagesModal: React.FC<PropsExternalImagesModal> = (
  props,
) => {
  const { srcValue, open, close, maxCount, onChange } = props;
  const [form] = Form.useForm();
  React.useEffect(() => {
    if (open) {
      form.setFieldsValue({
        items: srcValue.map(({ url, label }) => ({ id: url, label })),
      } satisfies FormData);
    }
  }, [open]);

  const items: ZSidLabel[] = Form.useWatch("items", form) ?? [];
  const frames: KeyedImgFrame[] = items.map(({ id, label }, i) => ({
    key: `${i}:${id}`, // Нет гарантий, что id уникален
    url: id,
    label,
  }));

  return (
    <Modal
      open={open}
      onCancel={close}
      title="Изменение изображений"
      centered
      width="70vw"
      okText="Применить"
      okButtonProps={{
        htmlType: "submit",
      }}
      modalRender={(content) => (
        <Form<FormData>
          layout="vertical"
          form={form}
          onFinish={(values: FormData) => {
            onChange(values.items);
            close();
          }}
        >
          {content}
        </Form>
      )}
    >
      <Form.List name="items">
        {(fields, { add, remove, move }) => (
          <div className={styles.formContent}>
            <div className={styles.listHeader}>
              <div className={styles.listTitle}>
                {countMessage(fields.length, maxCount)}
              </div>
              <Button
                icon={<PlusOutlined />}
                disabled={!!maxCount && fields.length >= maxCount}
                onClick={() => {
                  const focusId = `items_${fields.length}_id`;
                  add({ id: "", label: "" });
                  setTimeout(() => {
                    document.getElementById(focusId)?.focus();
                  }, 100);
                }}
              >
                Добавить
              </Button>
            </div>
            <div className={styles.listContent}>
              {fields.map(({ key, name }, i) => (
                <div key={key} className={styles.listItem}>
                  <div style={{ gridRow: `span 2` }} className={styles.thumb}>
                    <Thumb index={i} frames={frames} />
                  </div>
                  <Form.Item
                    name={[name, "id"]}
                    rules={[
                      {
                        required: true,
                        message: "Требуется ввести URL изображения",
                      },
                      {
                        pattern: /^https:\/\//i,
                        message: "Разрешены только https-ссылки",
                      },
                    ]}
                  >
                    <Input
                      style={{ width: "100%" }}
                      allowClear
                      placeholder="https://"
                    />
                  </Form.Item>

                  <Button
                    icon={<DeleteOutlined />}
                    danger
                    title="Удалить"
                    onClick={() => remove(i)}
                  />

                  <Form.Item name={[name, "label"]}>
                    <Input
                      style={{ width: "100%" }}
                      allowClear
                      placeholder="Название изображения"
                    />
                  </Form.Item>

                  {i > 0 && (
                    <Button
                      icon={<ArrowUpOutlined />}
                      title="Переместить в начало"
                      onClick={() => move(i, 0)}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Form.List>
    </Modal>
  );
};

const countMessage = (count: number, maxCount: number | undefined): string => {
  let msg = `Количество: ${count}`;
  if (maxCount) {
    msg += `. Всего возможно: ${maxCount}`;
  }
  return msg;
};

type PropsThumb = {
  frames: KeyedImgFrame[];
  index: number;
};

const Thumb: React.FC<PropsThumb> = ({ frames, index }) => {
  const url = frames[index]?.url ?? "";
  if (!/^https:\/\//i.test(url)) {
    return <FileImageOutlined className={styles.badImage} />;
  }
  return (
    <ImagePreview
      maxWidth="100%"
      maxHeight="100%"
      frames={frames}
      startIndex={index}
    />
  );
};
